import type { Languages } from '@s4l/i18n/types/Languages'
import type { Config } from '../../_config'
import { FactoryCookie } from '../../_cookie/FactoryCookie'
import type { Countries } from '../../_kernel/Country'
import { PriceType } from '../../_kernel/PriceType'
import type { AffiliateType } from '../domain/Affiliate'
import { Affiliate } from '../domain/Affiliate'
import { Language } from '../domain/Language'
import { Market } from '../domain/Market'
import type { ALLOWED_MARKETS, Markets } from '../domain/Market'
import { ForbiddenSharedRepository } from './ForbiddenSharedRepository'

type COOKIE_TRACK = {
  adults: string
  aid: AffiliateType
  ci: string
  co: string
  children: string
  lang: Languages
  market: Markets
  country: Countries
  slug: string
  rm?: 'all_in' | 'all_in_daily' | 'net' | 'gross' | 'trivago_net' | undefined
}
export class CookieSharedRepository extends ForbiddenSharedRepository {
  static MARKET_COOKIE_NAME = 'sfl_mkt'
  static COOKIE_LANGUAGE = 'lang'
  static COOKIE_TRACK = 'track'

  static create({ config }: { config: Config }) {
    return new CookieSharedRepository(config)
  }

  constructor(config: Config) {
    super(config)
  }

  async market() {
    const cookie = await FactoryCookie.create({ config: this.config }).cookie()

    const [error, market] = cookie.get<(typeof ALLOWED_MARKETS)[number]>({
      key: CookieSharedRepository.MARKET_COOKIE_NAME,
    })

    if (error) return Market.empty()
    if (!market) return Market.empty()

    return Market.create({ value: market })
  }

  async language() {
    const cookie = await FactoryCookie.create({ config: this.config }).cookie()

    const [error, language] = cookie.get<Languages>({ key: CookieSharedRepository.COOKIE_LANGUAGE })

    if (error) return Language.empty()
    if (!language) return Language.empty()

    return Language.create({ value: language })
  }

  async affiliate(): Promise<Affiliate> {
    const cookie = await FactoryCookie.create({ config: this.config }).cookie()

    const [error, trackJson] = cookie.get<string>({
      key: CookieSharedRepository.COOKIE_TRACK,
      encoding: 'base64',
    })

    if (!trackJson) return Affiliate.default()
    if (error) return Affiliate.default()

    try {
      const track = JSON.parse(trackJson) as COOKIE_TRACK
      if (!track.aid) return Affiliate.default()

      return Affiliate.create({
        value: track.aid,
        priceType: PriceType.createOrUndefinedFromCookie({ value: track.rm }),
      })
    } catch (e) {
      return Affiliate.default()
    }
  }
}
